// Quelle: /opt/lilasprojekte/frontend/src/components/app/ScreenBoundary.tsx (Stand 2026-09-29)
// Geändert: Texte deutsch aus lib/texte; zeigt `fehler.message` NUR für ApiError (der deutsche
// Satz des Servers), sonst „Hier ist etwas schiefgegangen. Die anderen Bereiche funktionieren
// weiter.“ — nie einen Stapel oder Servernamen (DESIGN §4.1 „Error“). In die Konsole geht nur
// die Fehlerklasse, nie der Wortlaut (E13: im Text könnte Kinderschrift stehen).
// Neu (Fehlerbericht 01.10.): componentDidCatch meldet den Absturz an POST /api/oberflaeche/fehler
// (lib/fehlerbericht: Routenmuster, Klasse, entschärfte Meldung, Komponentenstapel — kein Inhalt).
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Btn } from "../ui";
import { ApiError } from "../../lib/fetchJson";
import { chunkRiegelLoesen } from "../../lib/chunk";
import { fehlerMelden } from "../../lib/fehlerbericht";
import { TX } from "../../lib/texte";

interface Props {
  children: ReactNode;
  /** Ersetzt den Standardsatz. */
  titel?: string;
  /** Zusätzlicher Knopf neben „Neu laden“ und „Noch einmal“. */
  aktion?: ReactNode;
  /** Klein und ohne Karte (z. B. um den Chat in der Schale). */
  still?: boolean;
  /** Ändert sich dieser Wert, ist ein gefangener Absturz vergessen und die Kinder zeichnen neu —
   *  OHNE Neu-Einhängen (anders als `key`). Für die Dokumentansicht: der Korb hängt je Dokument
   *  (Ansichtszustand bleibt beim Blättern), der Absturz einer Seite darf aber nicht das ganze
   *  Dokument sperren (Review E26) — darum Pfad + Suche als Rücksetzwert. */
  zuruecksetzenBei?: string;
}
interface State {
  fehler: Error | null;
}

/**
 * Der Auffangkorb. Ohne ihn nimmt ein einziger Fehler beim Zeichnen den ganzen
 * Baum mit und hinterlässt eine weiße Seite. In App.tsx liegt er ÜBER dem
 * Router-Outlet, aber UNTER der Kopfleiste: stürzt ein Bildschirm ab, soll man
 * wegnavigieren können, statt neu laden zu müssen.
 *
 * Muss eine Klasse sein: `componentDidCatch` hat in Funktionskomponenten bis
 * heute keine Entsprechung.
 */
export class ScreenBoundary extends Component<Props, State> {
  override state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  override componentDidUpdate(vorher: Props, vorherZustand: State): void {
    // Nur einen Fehler vergessen, der schon VOR dieser Aktualisierung stand: stürzt gerade der
    // neue Weg selbst ab, bliebe es sonst bei einem Kreis aus Zurücksetzen und Absturz
    // (dasselbe Muster wie react-error-boundary `resetKeys`).
    if (this.state.fehler && vorherZustand.fehler && vorher.zuruecksetzenBei !== this.props.zuruecksetzenBei) this.setState({ fehler: null });
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // Nur Klasse und Ort — nie die Meldung selbst (sie könnte Wortlaut tragen).
    console.error("[Handschrift] Bildschirm abgestürzt:", fehler.name, info.componentStack?.split("\n")[1]?.trim() ?? "");
    // An den Server (Fehlerbericht 01.10.: der Absturz stand nirgends im Protokoll). Ein
    // ApiError ist eine Serverantwort und steht dort schon — der wird nicht noch einmal gemeldet.
    if (!(fehler instanceof ApiError)) fehlerMelden(fehler, info.componentStack, this.props.still ? "still" : "bildschirm");
  }

  override render() {
    const f = this.state.fehler;
    if (!f) return this.props.children;
    if (this.props.still) return null;
    const satz = f instanceof ApiError ? f.message : (this.props.titel ?? TX.fehler.umgefallen);
    return (
      <div className="card hinweisseite" role="alert">
        <p style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{satz}</p>
        <div className="reihe wrap" style={{ justifyContent: "center", marginTop: 20 }}>
          <Btn
            type="button"
            icon="arrows-clockwise"
            onClick={() => {
              chunkRiegelLoesen();
              window.location.reload();
            }}
          >
            {TX.fehler.neuLaden}
          </Btn>
          <Btn type="button" variant="ghost" onClick={() => this.setState({ fehler: null })}>
            {TX.fehler.nochmal}
          </Btn>
          {this.props.aktion}
        </div>
      </div>
    );
  }
}
